/**
 * @file usePlanSummary.ts
 * @brief This account's plan (planSummary.ts) for the pill, the Billing
 *        card and the plan dialog. Null for a super admin, who has no plan.
 *
 * @details
 * One read: GET /billing/subscriptions (cached, shared with Billing). A
 * company admin's seat count falls back to the company's active members
 * (members and admins, as teamPlan.service counts seats) until the server
 * sends seats itself.
 *
 * @author DES
 * @date 21-10-2026
 */

import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/shared/api/client";
import { companyEndpoints } from "@/shared/api/endpoints";
import { useAuth } from "@/shared/auth/auth-context";
import { useMySubscription } from "@/features/my-billing/api";
import { billingAudienceFor } from "@/features/my-billing/billingErrors";
import { summarizePlan, type PlanSummary } from "./planSummary";

const SEAT_ROLES = new Set(["user", "admin", "company_admin"]);

/** Active company users who hold a seat (members and admins). */
export function useCompanySeatCount(enabled: boolean) {
  return useQuery<number | null>({
    queryKey: ["company", "seat-count"],
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const raw = (await apiGet<unknown>(companyEndpoints.users)) as { users?: { status?: string; role?: string }[] };
      const users = Array.isArray(raw?.users) ? raw.users : null;
      return users ? users.filter((u) => u.status === "active" && SEAT_ROLES.has(String(u.role))).length : null;
    },
  });
}

export function usePlanSummary(): { plan: PlanSummary | null; isLoading: boolean } {
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const audience = billingAudienceFor(user);
  const status = useMySubscription();
  const needsSeats =
    !isSuper && audience === "company_admin" && !!status.data && !status.data.companyPlan;
  const seats = useCompanySeatCount(needsSeats);
  if (isSuper || !user) return { plan: null, isLoading: false };
  if (!status.data) return { plan: null, isLoading: status.isLoading };
  return {
    plan: summarizePlan(status.data, audience, { memberCount: seats.data ?? null }),
    isLoading: false,
  };
}
